import React, { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SocketProvider } from './context/SocketContext';
import ProtectedRoute from './components/ProtectedRoute';
import PageLoader from './components/PageLoader';
import './i18n/config'; // Initialize i18n

// ── Always eager (tiny, needed immediately) ─────────────────────────────────
import Layout from './components/layout/Layout';
import Login from './pages/auth/Login';

// ── Lazy-loaded pages (split into separate JS chunks) ───────────────────────
// Auth
const Register             = lazy(() => import('./pages/auth/Register'));
const ForgotPassword       = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword        = lazy(() => import('./pages/auth/ResetPassword'));
const ChangePassword       = lazy(() => import('./pages/auth/ChangePassword'));

// Super Admin
const SuperAdminSetup      = lazy(() => import('./pages/superadmin/SuperAdminSetup'));
const SuperAdminDashboard  = lazy(() => import('./pages/superadmin/SuperAdminDashboard'));
const CreateGym            = lazy(() => import('./pages/superadmin/CreateGym'));
const GymDetails           = lazy(() => import('./pages/superadmin/GymDetails'));
const PermissionManagement  = lazy(() => import('./pages/superadmin/PermissionManagement'));
const SystemAnalytics       = lazy(() => import('./pages/superadmin/SystemAnalytics'));
const SubscriptionManager   = lazy(() => import('./pages/superadmin/SubscriptionManager'));
const AnnouncementBroadcast = lazy(() => import('./pages/superadmin/AnnouncementBroadcast'));
const ChatMonitor           = lazy(() => import('./pages/superadmin/ChatMonitor'));

// Core
const Dashboard            = lazy(() => import('./pages/Dashboard'));
const QrCheckIn            = lazy(() => import('./pages/QrCheckIn'));
const NotFound             = lazy(() => import('./pages/errors/NotFound'));

// Members
const MembersList          = lazy(() => import('./pages/members/MembersList'));
const AddMember            = lazy(() => import('./pages/members/AddMember'));
const MemberDetails        = lazy(() => import('./pages/members/MemberDetails'));
const EditMember           = lazy(() => import('./pages/members/EditMember'));
const RenewalManagement    = lazy(() => import('./pages/admin/RenewalManagement'));

// Plans / Payments
const PlansManagement      = lazy(() => import('./pages/plans/PlansManagement'));
const PaymentsList         = lazy(() => import('./pages/payments/PaymentsList'));
const AddPayment           = lazy(() => import('./pages/payments/AddPayment'));
const PaymentDetails       = lazy(() => import('./pages/payments/PaymentDetails'));
const PaymentPage          = lazy(() => import('./pages/payment/PaymentPage'));
const PaymentReports       = lazy(() => import('./pages/reports/PaymentReports'));

// Attendance
const AttendanceTracking   = lazy(() => import('./pages/attendance/AttendanceTracking'));
const AttendanceReport     = lazy(() => import('./pages/attendance/AttendanceReport'));
const BiometricCheckIn     = lazy(() => import('./pages/attendance/BiometricCheckIn'));

// Users / Profile / Settings
const UsersList            = lazy(() => import('./pages/users/UsersList'));
const AddUser              = lazy(() => import('./pages/users/AddUser'));
const UserLoginManagement  = lazy(() => import('./pages/users/UserLoginManagement'));
const Profile              = lazy(() => import('./pages/account/Profile'));
const Settings             = lazy(() => import('./pages/settings/Settings'));

// Trainers
const TrainerManagement    = lazy(() => import('./pages/trainers/TrainerManagement'));
const TrainerProfiles      = lazy(() => import('./pages/trainers/TrainerProfiles'));
const TrainerBookings      = lazy(() => import('./pages/trainers/TrainerBookings'));
const TrainerRevenue       = lazy(() => import('./pages/trainers/TrainerRevenue'));

// Inventory / POS
const InventoryManagement  = lazy(() => import('./pages/inventory/InventoryManagement'));
const ProductBookings      = lazy(() => import('./pages/inventory/ProductBookings'));
const InventoryReports     = lazy(() => import('./pages/inventory/InventoryReports'));
const POSSystem            = lazy(() => import('./pages/pos/POSSystem'));
const ProductSalesReport   = lazy(() => import('./pages/reports/ProductSalesReport'));

// AI + Chat + Campaigns
const AIFitnessCoach       = lazy(() => import('./pages/ai-coach/AIFitnessCoach'));
const Chat                 = lazy(() => import('./pages/Chat'));
const Campaigns            = lazy(() => import('./pages/admin/Campaigns'));
const AdminAnnouncements   = lazy(() => import('./pages/admin/Announcements'));

// Expenses
const ExpenseManagement    = lazy(() => import('./pages/expenses/ExpenseManagement'));

// Analytics
const Analytics            = lazy(() => import('./pages/analytics/Analytics'));

// Ops (admin) + AI Assistant (member)
const Worklist             = lazy(() => import('./pages/ops/Worklist'));
const LiveBoard            = lazy(() => import('./pages/ops/LiveBoard'));
const AiAssistant          = lazy(() => import('./pages/member/AiAssistant'));

// Renewal Journey (admin) + Progress (member)
const RenewalJourney       = lazy(() => import('./pages/renewal/RenewalJourney'));
const Progress             = lazy(() => import('./pages/member/Progress'));

// Member-facing pages
const MemberDashboard      = lazy(() => import('./pages/member/MemberDashboard'));
const ProductCatalog       = lazy(() => import('./pages/member/ProductCatalog'));
const MyBookings           = lazy(() => import('./pages/member/MyBookings'));
const MembershipRenewal    = lazy(() => import('./pages/member/MembershipRenewal'));
const PersonalTrainer      = lazy(() => import('./pages/member/PersonalTrainer'));
const Referrals            = lazy(() => import('./pages/member/Referrals'));
const MemberRewards        = lazy(() => import('./pages/member/Rewards'));
const RewardsCatalog       = lazy(() => import('./pages/member/RewardsCatalog'));
const AdminGamification    = lazy(() => import('./pages/admin/Gamification'));
const AdminEmailSettings   = lazy(() => import('./pages/admin/EmailSettings'));
const RewardsManagement    = lazy(() => import('./pages/admin/RewardsManagement'));
const EnquiryManagement    = lazy(() => import('./pages/admin/EnquiryManagement'));
const SuperAdminGamification = lazy(() => import('./pages/superadmin/Gamification'));

// ── App ─────────────────────────────────────────────────────────────────────
const App = () => (
  <SocketProvider>
    <ToastContainer position="top-right" autoClose={3000} />
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Super Admin */}
        <Route path="/superadmin/setup"      element={<SuperAdminSetup />} />
        <Route path="/superadmin/dashboard"  element={<SuperAdminDashboard />} />
        <Route path="/superadmin/gyms/create" element={<CreateGym />} />
        <Route path="/superadmin/gyms/:id"   element={<GymDetails />} />
        <Route path="/permissions"                     element={<PermissionManagement />} />
        <Route path="/superadmin/analytics"          element={<SystemAnalytics />} />
        <Route path="/superadmin/subscriptions"      element={<SubscriptionManager />} />
        <Route path="/superadmin/announcements"      element={<AnnouncementBroadcast />} />
        <Route path="/superadmin/chat-monitor"       element={<ChatMonitor />} />
          <Route path="/superadmin/gamification"       element={<SuperAdminGamification />} />

        {/* Public */}
        <Route path="/qr-checkin"            element={<QrCheckIn />} />
        <Route path="/login"                 element={<Login />} />
        <Route path="/register"              element={<Register />} />
        <Route path="/forgot-password"       element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/payment"               element={<PaymentPage />} />
        <Route path="/change-password"       element={<ChangePassword />} />

        {/* Protected (Layout wrapper) */}
        <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route index element={<Dashboard />} />

          {/* Members */}
          <Route path="members">
            <Route index element={<ProtectedRoute requiredMenu="members"><MembersList /></ProtectedRoute>} />
            <Route path="add" element={<ProtectedRoute requiredMenu="members"><AddMember /></ProtectedRoute>} />
            <Route path=":id" element={<ProtectedRoute requiredMenu="members"><MemberDetails /></ProtectedRoute>} />
            <Route path="edit/:id" element={<ProtectedRoute requiredMenu="members"><EditMember /></ProtectedRoute>} />
          </Route>

          <Route path="renewal-management" element={<ProtectedRoute requiredMenu="renewalmanagement"><RenewalManagement /></ProtectedRoute>} />
          <Route path="plans"              element={<ProtectedRoute requiredMenu="plans"><PlansManagement /></ProtectedRoute>} />

          {/* Payments */}
          <Route path="payments">
            <Route index element={<ProtectedRoute requiredMenu="payments"><PaymentsList /></ProtectedRoute>} />
            <Route path="add" element={<ProtectedRoute requiredMenu="payments"><AddPayment /></ProtectedRoute>} />
            <Route path=":id" element={<ProtectedRoute requiredMenu="payments"><PaymentDetails /></ProtectedRoute>} />
          </Route>

          {/* Attendance */}
          <Route path="attendance">
            <Route index element={<ProtectedRoute requiredMenu="attendance"><AttendanceTracking /></ProtectedRoute>} />
            <Route path="reports"   element={<ProtectedRoute requiredMenu="reports"><AttendanceReport /></ProtectedRoute>} />
            <Route path="biometric" element={<ProtectedRoute requiredMenu="attendance"><BiometricCheckIn /></ProtectedRoute>} />
          </Route>

          {/* Users */}
          <Route path="users">
            <Route index element={<ProtectedRoute requiredMenu="trainers"><UsersList /></ProtectedRoute>} />
            <Route path="add" element={<ProtectedRoute requiredMenu="trainers"><AddUser /></ProtectedRoute>} />
            <Route path="login-management" element={<ProtectedRoute requiredMenu="trainers"><UserLoginManagement /></ProtectedRoute>} />
          </Route>

          <Route path="profile"  element={<Profile />} />
          <Route path="settings" element={<ProtectedRoute requiredMenu="settings"><Settings /></ProtectedRoute>} />

          {/* Reports */}
          <Route path="reports">
            <Route index        element={<ProtectedRoute requiredMenu="reports"><AttendanceReport /></ProtectedRoute>} />
            <Route path="payments"      element={<ProtectedRoute requiredMenu="reports"><PaymentReports /></ProtectedRoute>} />
            <Route path="product-sales" element={<ProtectedRoute requiredMenu="reports"><ProductSalesReport /></ProtectedRoute>} />
          </Route>

          <Route path="ai-coach" element={<ProtectedRoute requiredMenu="aicoach"><AIFitnessCoach /></ProtectedRoute>} />

          {/* Inventory & POS */}
          <Route path="inventory"          element={<ProtectedRoute requiredMenu="inventory"><InventoryManagement /></ProtectedRoute>} />
          <Route path="inventory/bookings" element={<ProtectedRoute requiredMenu="inventory"><ProductBookings /></ProtectedRoute>} />
          <Route path="inventory/reports"  element={<ProtectedRoute requiredMenu="reports"><InventoryReports /></ProtectedRoute>} />
          <Route path="pos"                element={<ProtectedRoute requiredMenu="pos"><POSSystem /></ProtectedRoute>} />

          {/* Trainers */}
          <Route path="trainer-management" element={<ProtectedRoute requiredMenu="trainermanagement"><TrainerManagement /></ProtectedRoute>} />
          <Route path="trainer-profiles"   element={<ProtectedRoute requiredMenu="trainermanagement"><TrainerProfiles /></ProtectedRoute>} />
          <Route path="trainer-bookings"   element={<ProtectedRoute requiredMenu="trainermanagement"><TrainerBookings /></ProtectedRoute>} />
          <Route path="trainer-revenue"    element={<ProtectedRoute requiredMenu="trainermanagement"><TrainerRevenue /></ProtectedRoute>} />

          {/* New features */}
          <Route path="chat"          element={<ProtectedRoute><Chat /></ProtectedRoute>} />
          <Route path="campaigns"     element={<ProtectedRoute><Campaigns /></ProtectedRoute>} />
          <Route path="referrals"     element={<ProtectedRoute><Referrals /></ProtectedRoute>} />
          <Route path="member/rewards" element={<ProtectedRoute><MemberRewards /></ProtectedRoute>} />
          <Route path="member/rewards-catalog" element={<ProtectedRoute><RewardsCatalog /></ProtectedRoute>} />
          <Route path="admin/gamification" element={<ProtectedRoute requiredMenu="gamification"><AdminGamification /></ProtectedRoute>} />
          <Route path="admin/rewards" element={<ProtectedRoute requiredMenu="gamification"><RewardsManagement /></ProtectedRoute>} />
          <Route path="admin/email-settings" element={<ProtectedRoute requiredMenu="settings"><AdminEmailSettings /></ProtectedRoute>} />
          <Route path="announcements" element={<ProtectedRoute><AdminAnnouncements /></ProtectedRoute>} />

          {/* Expenses */}
          <Route path="expenses"  element={<ProtectedRoute requiredMenu="expenses"><ExpenseManagement /></ProtectedRoute>} />

          {/* Analytics (admin only) */}
          <Route path="analytics" element={<ProtectedRoute adminOnly><Analytics /></ProtectedRoute>} />

          {/* Ops (admin only) */}
          <Route path="worklist"   element={<ProtectedRoute adminOnly><Worklist /></ProtectedRoute>} />
          <Route path="live-board" element={<ProtectedRoute adminOnly><LiveBoard /></ProtectedRoute>} />

          {/* Renewal Journey (admin only) */}
          <Route path="renewal-journey" element={<ProtectedRoute adminOnly><RenewalJourney /></ProtectedRoute>} />

          {/* Enquiries */}
          <Route path="enquiries" element={<ProtectedRoute requiredMenu="enquiries"><EnquiryManagement /></ProtectedRoute>} />

          {/* Member-facing */}
          <Route path="member">
            <Route index            element={<ProtectedRoute requiredMenu="dashboard"><MemberDashboard /></ProtectedRoute>} />
            <Route path="dashboard" element={<ProtectedRoute requiredMenu="dashboard"><MemberDashboard /></ProtectedRoute>} />
            <Route path="products"  element={<ProtectedRoute requiredMenu="products"><ProductCatalog /></ProtectedRoute>} />
            <Route path="bookings"  element={<ProtectedRoute requiredMenu="bookings"><MyBookings /></ProtectedRoute>} />
            <Route path="personal-trainer" element={<ProtectedRoute><PersonalTrainer /></ProtectedRoute>} />
            <Route path="renewal"   element={<ProtectedRoute><MembershipRenewal /></ProtectedRoute>} />
            <Route path="progress"  element={<ProtectedRoute><Progress /></ProtectedRoute>} />
            <Route path="fingerprint-attendance" element={<ProtectedRoute><BiometricCheckIn /></ProtectedRoute>} />
            <Route path="assistant" element={<ProtectedRoute><AiAssistant /></ProtectedRoute>} />
          </Route>
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  </SocketProvider>
);

export default App;
